import React, { useMemo } from 'react';
import Select, { SingleValue } from 'react-select';
import { Shipper } from '../../types';
import { useDarkMode } from '../../hooks/useDarkMode';
import { getSelectStyles } from './selectStyles';

interface ShipperSelectProps {
  shippers: Shipper[];
  value: string;
  onChange: (shipperId: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface ShipperOption {
  value: string;
  label: string;
}

export const ShipperSelect: React.FC<ShipperSelectProps> = ({
  shippers,
  value,
  onChange,
  placeholder = 'Select a shipper',
  disabled = false,
}) => {
  const { isDarkMode } = useDarkMode();

  const options: ShipperOption[] = useMemo(
    () =>
      shippers.map((shipper) => ({
        value: shipper.id,
        label: `${shipper.code} - ${shipper.shortname || shipper.name}`,
      })),
    [shippers]
  );

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value]
  );

  const customStyles = useMemo(
    () => getSelectStyles<ShipperOption>(isDarkMode),
    [isDarkMode]
  );

  const handleChange = (newValue: SingleValue<ShipperOption>) => {
    if (newValue) {
      onChange(newValue.value);
    } else {
      onChange('');
    }
  };

  return (
    <Select<ShipperOption, false>
      options={options}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder}
      isDisabled={disabled}
      isClearable
      isSearchable
      noOptionsMessage={() => 'No shippers found'}
      styles={customStyles}
      className="react-select-container"
      classNamePrefix="react-select"
    />
  );
};
